import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, LogOut } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSEO } from "@/components/Seo";
import { ImageField } from "@/components/ImageField";

const inputCls =
  "w-full border border-white/15 bg-transparent px-3 py-2 text-sm text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-[#D4AF37]";
const labelCls = "mb-1 block text-[10px] uppercase tracking-[0.25em] text-white/40";
const smallBtn =
  "border border-white/20 p-2 text-white/60 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]";

const PRODUCT_FIELDS = [
  { name: "title", label: "Title" },
  { name: "description", label: "Description", type: "textarea" },
  { name: "price", label: "Price (e.g. $38)" },
  { name: "etsy_url", label: "Etsy URL (leave empty if not ready)" },
  { name: "image_url", label: "Image URL (leave empty for thread placeholder)" },
  { name: "kind", label: "Section", type: "select", options: [["apparel", "Apparel"], ["print", "Print"]] },
  { name: "order", label: "Sort order", type: "number" },
  { name: "featured", label: "Featured on home", type: "checkbox" },
  { name: "active", label: "Visible on site", type: "checkbox" },
];

const ARTWORK_FIELDS = [
  { name: "title", label: "Title" },
  { name: "description", label: "Description", type: "textarea" },
  { name: "image_url", label: "Image URL (leave empty for thread placeholder)" },
  {
    name: "pillar",
    label: "Pillar",
    type: "select",
    options: [
      ["", "Untagged"],
      ["freedom", "Freedom"],
      ["chaos", "Chaos"],
      ["truth", "Truth"],
      ["grit", "Grit"],
      ["rebellion", "Rebellion"],
      ["wisdom", "Wisdom"],
    ],
  },
  { name: "order", label: "Sort order", type: "number" },
  { name: "active", label: "Visible on site", type: "checkbox" },
];

const emptyProduct = { title: "", description: "", price: "", etsy_url: "", image_url: "", order: 0, featured: false, active: true };
const emptyArtwork = { title: "", description: "", image_url: "", pillar: "", order: 0, active: true };

function CrudSection({ endpoint, fields, empty, testid }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/${endpoint}`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editingId) await api.put(`/admin/${endpoint}/${editingId}`, payload);
      else await api.post(`/admin/${endpoint}`, payload);
      toast.success(editingId ? "Updated" : "Created");
      setForm(empty);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/${endpoint}/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="grid gap-12 lg:grid-cols-2" data-testid={`${testid}-manager`}>
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/30">
          {items.length} item{items.length === 1 ? "" : "s"}
        </p>
        <div className="mt-4">
          {items.map((it) => (
            <div
              key={it.id}
              className="flex items-center justify-between gap-4 border-b border-white/10 py-3"
              data-testid={`${testid}-row-${it.id}`}
            >
              <div className="min-w-0">
                <p className="truncate font-serif text-lg font-light">
                  {it.title}
                  {it.price ? <span className="ml-3 text-sm text-[#D4AF37]">{it.price}</span> : null}
                  {!it.active && <span className="ml-3 text-[10px] uppercase tracking-widest text-white/30">hidden</span>}
                </p>
                <p className="truncate text-xs text-white/40">{it.etsy_url || it.image_url || "thread placeholder artwork"}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  className={smallBtn}
                  aria-label="Edit"
                  data-testid={`${testid}-edit-${it.id}`}
                  onClick={() => {
                    setEditingId(it.id);
                    setForm({ ...empty, ...it });
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button className={smallBtn} aria-label="Delete" data-testid={`${testid}-delete-${it.id}`} onClick={() => remove(it.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="py-6 text-sm text-white/30">Nothing here yet.</p>}
        </div>
      </div>
      <form onSubmit={save} className="h-fit border border-white/10 p-6">
        <p className="font-serif text-xl font-light">{editingId ? "Edit" : "Add new"}</p>
        <div className="mt-5 space-y-4">
          {fields.map((f) =>
            f.name === "image_url" ? (
              <div key={f.name}>
                <label className={labelCls}>{f.label}</label>
                <ImageField
                  value={form[f.name] ?? ""}
                  onChange={(v) => setForm({ ...form, [f.name]: v })}
                  testid={`${testid}-field-${f.name}`}
                />
              </div>
            ) : f.type === "select" ? (
              <div key={f.name}>
                <label className={labelCls}>{f.label}</label>
                <select
                  className={`${inputCls} bg-[#0A0A0B]`}
                  value={form[f.name] ?? f.options[0][0]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  data-testid={`${testid}-field-${f.name}`}
                >
                  {f.options.map(([val, label]) => (
                    <option key={val} value={val}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            ) : f.type === "checkbox" ? (
              <label key={f.name} className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-white/60">
                <input
                  type="checkbox"
                  checked={!!form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.checked })}
                  className="h-4 w-4 accent-[#D4AF37]"
                  data-testid={`${testid}-field-${f.name}`}
                />
                {f.label}
              </label>
            ) : (
              <div key={f.name}>
                <label className={labelCls}>{f.label}</label>
                {f.type === "textarea" ? (
                  <textarea
                    rows={2}
                    className={inputCls}
                    value={form[f.name] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    data-testid={`${testid}-field-${f.name}`}
                  />
                ) : (
                  <input
                    type={f.type || "text"}
                    className={inputCls}
                    value={form[f.name] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    data-testid={`${testid}-field-${f.name}`}
                  />
                )}
              </div>
            )
          )}
        </div>
        <div className="mt-6 flex gap-3">
          <button
            type="submit"
            data-testid={`${testid}-save-button`}
            className="border border-[#D4AF37]/70 px-6 py-2.5 text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#D4AF37] hover:text-black"
          >
            {editingId ? "Save changes" : "Create"}
          </button>
          {editingId && (
            <button
              type="button"
              data-testid={`${testid}-cancel-button`}
              onClick={() => {
                setEditingId(null);
                setForm(empty);
              }}
              className="border border-white/20 px-6 py-2.5 text-[10px] uppercase tracking-[0.25em] text-white/60 transition-colors duration-300 hover:border-white hover:text-white"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

const SETTINGS_FIELDS = [
  { name: "substack_url", label: "Substack URL" },
  { name: "contact_email", label: "Contact email" },
  { name: "instagram_url", label: "Instagram URL" },
  { name: "facebook_url", label: "Facebook URL" },
  { name: "twitter_url", label: "Twitter / X URL" },
  { name: "tiktok_url", label: "TikTok URL" },
];

function SettingsSection() {
  const [form, setForm] = useState({});

  useEffect(() => {
    api.get("/settings/public").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <form onSubmit={save} className="max-w-xl border border-white/10 p-6" data-testid="settings-form">
      <p className="font-serif text-xl font-light">Site settings</p>
      <div className="mt-5 space-y-4">
        {SETTINGS_FIELDS.map((f) => (
          <div key={f.name}>
            <label className={labelCls}>{f.label}</label>
            <input
              className={inputCls}
              value={form[f.name] ?? ""}
              onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
              data-testid={`settings-field-${f.name}`}
            />
          </div>
        ))}
      </div>
      <button
        type="submit"
        data-testid="settings-save-button"
        className="mt-6 border border-[#D4AF37]/70 px-6 py-2.5 text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#D4AF37] hover:text-black"
      >
        Save settings
      </button>
    </form>
  );
}

export default function Admin() {
  useSEO("Studio Admin | Threads Unbound™", "Threads Unbound studio admin.");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0A0B] text-sm text-white/40">
        Checking access…
      </div>
    );
  }

  const tabs = [
    ["products", "Products"],
    ["artworks", "Artworks"],
    ["settings", "Settings"],
  ];

  return (
    <main className="min-h-screen bg-[#0A0A0B] px-6 py-10 text-white md:px-12" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-serif text-3xl font-light">
          Studio <span className="italic text-[#D4AF37]">Admin</span>
        </h1>
        <div className="flex items-center gap-3">
          <Link
            to="/"
            data-testid="admin-view-site"
            className="border border-white/20 px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-white/60 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
          >
            View site
          </Link>
          <button
            data-testid="admin-logout"
            onClick={async () => {
              await logout();
              navigate("/admin/login");
            }}
            className="flex items-center gap-2 border border-white/20 px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-white/60 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
          >
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </div>

      <div className="mt-10 flex gap-6 border-b border-white/10">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            data-testid={`admin-tab-${key}`}
            onClick={() => setTab(key)}
            className={`pb-3 text-xs uppercase tracking-[0.25em] transition-colors duration-300 ${
              tab === key ? "border-b border-[#D4AF37] text-[#D4AF37]" : "text-white/40 hover:text-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-10 pb-20">
        {tab === "products" && <CrudSection endpoint="products" fields={PRODUCT_FIELDS} empty={emptyProduct} testid="product" />}
        {tab === "artworks" && <CrudSection endpoint="artworks" fields={ARTWORK_FIELDS} empty={emptyArtwork} testid="artwork" />}
        {tab === "settings" && <SettingsSection />}
      </div>
    </main>
  );
}
